import { ReactElement } from 'react'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'
import axios from 'src/utilities/http-client'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'

import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Button from '@mui/material/Button'
import { CancelIcon, SubmitIcon } from 'src/v3/core/icons'
import DialogActions from '@mui/material/DialogActions'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  fetchLinkedServices,
  selectSelectedLinkedService,
} from '../../admin.slice'
import { fetchLinkedServices as fetchManageLinkedServices } from '../../../manage/manage.slice'

interface UserCapFormFields {
  userCap: number
}

interface UserCapFormProps {
  isOpen: boolean
  userCap?: number
  onClose: () => void
  isSaving: (bool: boolean) => void
  onError?: (error: string) => void
}

export let UserCapForm = ({
  isOpen,
  userCap,
  onClose,
  isSaving,
  onError,
}: UserCapFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let selectedLinkedService = useAppSelector(selectSelectedLinkedService)

  let formValues: UserCapFormFields = {
    userCap: userCap || 0,
  }

  let validationSchema = z.object({
    userCap: z.number().min(0, 'User Cap is Required'),
  })

  let form = useForm({
    defaultValues: formValues,
    validators: {
      onChange: validationSchema,
    },
    onSubmit: async ({ value }) => {
      onClose()
      isSaving(true)
      axios
        .put('/api/admin/linked-service/user-cap', {
          id: selectedLinkedService,
          userCap: value.userCap === 0 ? null : value.userCap,
        })
        .then(() => {
          dispatch(fetchLinkedServices())
          dispatch(fetchManageLinkedServices())
          form.reset()
          isSaving(false)
        })
        .catch((err) => {
          if (onError) {
            onError(
              (err?.response?.data || err?.message) ??
                'Error Updating User Cap',
            )
          }

          form.reset()
          isSaving(false)
          dispatch(fetchLinkedServices())
        })
    },
  })

  let handleOnClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={() => onClose()}>
      <Paper sx={{ minWidth: 300, p: 3 }}>
        <DialogTitle sx={{ textAlign: 'center', p: 0, mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Edit User Cap
          </Typography>
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent sx={{ p: 0, mb: 3 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
            >
              <form.Field
                name="userCap"
                children={(field) => (
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={field.state.value}
                    label="User Cap"
                    name={field.name}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    onBlur={field.handleBlur}
                    error={shouldShowZodFieldError(field.state.meta)}
                    helperText={
                      shouldShowZodFieldError(field.state.meta) &&
                      getZodValidationError(field.state.meta.errors)
                    }
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingBottom: 3,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isSubmitting || !state.isValid}
              children={(isDisabled) => (
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={isDisabled}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              onClick={handleOnClose}
              color="error"
              variant="contained"
              sx={{ flex: '1 1 0px' }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
